import { Typography } from '@mui/material';
import type { ComponentPropsWithoutRef } from 'react';

import type { SidekickRecipient } from '../../contexts/SidekickApiContext';
import useStyles from './RecipientOption.styles';
import { getRecipientName } from './utils';

/** Props for one suggestion row; MUI spreads the option props in. */
export type RecipientOptionProps = ComponentPropsWithoutRef<'li'> & {
  /** The directory match this row offers. */
  recipient: SidekickRecipient;
};

/**
 * Initials for the avatar.
 *
 * A named recipient is initialled from the first and last word of its
 * name. An unnamed one has nothing to draw from, so the first letter
 * of its address stands in.
 */
const getRecipientInitials = (recipient: SidekickRecipient): string => {
  const words = getRecipientName(recipient).split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return recipient.email.charAt(0).toUpperCase();
  }

  const last = words.length > 1 ? words[words.length - 1] : '';

  return `${words[0].charAt(0)}${last.charAt(0)}`.toUpperCase();
};

/**
 * One row of the recipients dropdown: an initials avatar beside the
 * recipient's name, with their address underneath.
 *
 * The address only earns its own line when there is a name above it,
 * since an unnamed recipient already shows its address as the name.
 *
 * @param recipient - The directory match this row offers.
 */
function RecipientOption({
  recipient,
  className,
  ...listItemProps
}: RecipientOptionProps) {
  const { classes, cx } = useStyles();
  const name = getRecipientName(recipient);

  return (
    <li {...listItemProps} className={cx(classes.row, className)}>
      {/* The initials repeat the name beside them, so a reader hears
          it once. */}
      <div className={classes.avatar} aria-hidden="true">
        <Typography variant="label_medium">
          {getRecipientInitials(recipient)}
        </Typography>
      </div>
      <div className={classes.details}>
        <Typography className={classes.name} variant="small_body_medium">
          {name === '' ? recipient.email : name}
        </Typography>
        {name !== '' && (
          <Typography className={classes.email} variant="caption_regular">
            {recipient.email}
          </Typography>
        )}
      </div>
    </li>
  );
}

export default RecipientOption;
